import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Box,
  Avatar,
  Grid,
  Chip,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore,
  FactCheck as FactCheckIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  AttachFile as AttachFileIcon,
  SaveOutlined as SaveOutlinedIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faHourglassHalf,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ReimbursementList = () => {
  const [reimbursements, setReimbursements] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState({});
  const [remarkError, setRemarkError] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const fetchReimbursements = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(`${BASE_URL}/api/reimbursement`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error fetching reimbursements");
        }

        const data = await response.json();
        setReimbursements(data);
      } catch (err) {
        console.error("Error fetching reimbursements:", err);
        setError("Error fetching reimbursements. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchReimbursements();
  }, []);

  const handleExpandClick = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleStatusChange = async (reimbursementId, expenseId, newStatus) => {
    const remark = remarks[expenseId] || "";

    try {
      const token = sessionStorage.getItem("token");

      const response = await fetch(
        `${BASE_URL}/api/reimbursement/update-expense-status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reimbursementId,
            expenseId,
            newStatus,
            remark,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error updating expense status");
      }

      const updatedReimbursements = reimbursements.map((reimbursement) => {
        if (reimbursement._id === reimbursementId) {
          reimbursement.expenses = reimbursement.expenses.map((expense) =>
            expense._id === expenseId
              ? { ...expense, status: newStatus, remark }
              : expense
          );
        }
        return reimbursement;
      });

      setReimbursements(updatedReimbursements);
      setRemarks((prev) => ({ ...prev, [expenseId]: "" }));
    } catch (error) {
      console.error("Error updating expense status:", error);
    }
  };

  const handleRemarkChange = (expenseId, value) => {
    setRemarks((prev) => ({ ...prev, [expenseId]: value }));
  };

  const displayAmount = (value) => {
    return value ? `\u20B9${value}` : "\u20B90";
  };

  // Compute stats
  const totalRequests = reimbursements.length;
  const pendingCount = reimbursements.filter((r) =>
    r.expenses?.some((e) => e.status === "pending")
  ).length;
  const approvedCount = reimbursements.filter((r) =>
    r.expenses?.every((e) => e.status === "approved")
  ).length;
  const rejectedCount = reimbursements.filter((r) =>
    r.expenses?.some((e) => e.status === "disapproved")
  ).length;

  // Derive overall status for a reimbursement
  const getOverallStatus = (reimbursement) => {
    const allApproved = reimbursement.expenses?.every(
      (e) => e.status === "approved"
    );
    const hasDisapproved = reimbursement.expenses?.some(
      (e) => e.status === "disapproved"
    );
    return allApproved ? "approved" : hasDisapproved ? "rejected" : "pending";
  };

  // Client-side filtering
  const filteredReimbursements = reimbursements.filter((r) => {
    const matchesSearch =
      searchQuery.trim() === "" ||
      r.employeeName?.toLowerCase().includes(searchQuery.toLowerCase());
    const overallStatus = getOverallStatus(r);
    const matchesStatus =
      statusFilter === "all" || overallStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Status border color mapping
  const statusBorderColor = {
    approved: "#43e97b",
    rejected: "#f5576c",
    pending: "#fccb90",
  };

  // Table header cell style
  const headerCellSx = {
    bgcolor: "#f5f7fb",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#5a5f7a",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    py: 1.5,
    whiteSpace: "nowrap",
    borderBottom: "2px solid #eef0f6",
  };

  const bodyCellSx = {
    fontSize: "0.875rem",
    color: "#1a1a2e",
    py: 2,
    borderBottom: "1px solid #f0f1f5",
  };

  // Inner table header for expense details
  const innerHeaderSx = {
    bgcolor: "#f8f9fc",
    fontWeight: 600,
    fontSize: "0.7rem",
    color: "#667eea",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    py: 1.2,
    whiteSpace: "nowrap",
    borderBottom: "2px solid #eef0f6",
  };

  const innerBodySx = {
    fontSize: "0.8rem",
    color: "#3a3a5c",
    py: 1.5,
  };

  return (
    <PageContainer>
      {/* Gradient Page Header */}
      <PageHeader
        gradient
        title="Reimbursement Approvals"
        subtitle="Review and manage employee reimbursement claims"
        icon={<ReceiptIcon />}
      />

      {/* Stats Row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Total Requests"
            value={totalRequests}
            icon={<FontAwesomeIcon icon={faFileInvoiceDollar} />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Pending"
            value={pendingCount}
            icon={<FontAwesomeIcon icon={faHourglassHalf} />}
            color="#D97706"
            bgColor="#FEF3C7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Approved"
            value={approvedCount}
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
            color="#059669"
            bgColor="#D1FAE5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Rejected"
            value={rejectedCount}
            icon={<FontAwesomeIcon icon={faTimesCircle} />}
            color="#DC2626"
            bgColor="#FEE2E2"
          />
        </Grid>
      </Grid>

      {/* Search & Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: 2.5,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          bgcolor: "#fff",
        }}
      >
        <TextField
          placeholder="Search by employee name..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#8b8fa3", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: { xs: "100%", sm: 280 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              fontSize: "0.875rem",
              bgcolor: "#f8f9fc",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#667eea",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#667eea",
                borderWidth: "2px",
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#eef0f6",
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <FilterListIcon sx={{ color: "#8b8fa3", fontSize: 20 }} />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: 160,
              fontSize: "0.875rem",
              borderRadius: "12px",
              bgcolor: "#f8f9fc",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#eef0f6",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#667eea",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#667eea",
                borderWidth: "2px",
              },
              "& .MuiSelect-select": {
                py: 1,
              },
            }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </Box>
      </Paper>

      {/* Main Table Card */}
      {loading ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#667eea", mb: 2 }} />
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
              Loading reimbursements...
            </Typography>
          </Box>
        </Paper>
      ) : error ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #FCA5A5",
            borderRadius: "16px",
            p: 3,
            bgcolor: "#FEF2F2",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#991B1B", fontWeight: 500 }}>
            {error}
          </Typography>
        </Paper>
      ) : reimbursements.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: "1px solid #eef0f6", borderRadius: "16px" }}
        >
          <EmptyState
            title="No reimbursement requests"
            subtitle="There are no pending reimbursement requests to review."
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          {/* Gradient Section Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 3.5,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 18,
                }}
              >
                <FactCheckIcon fontSize="small" />
              </Box>
              <Box>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "1rem", color: "#fff" }}
                >
                  All Requests
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  {filteredReimbursements.length} of {reimbursements.length}{" "}
                  reimbursement
                  {reimbursements.length !== 1 ? "s" : ""}
                  {(searchQuery || statusFilter !== "all") && " (filtered)"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Table */}
          {filteredReimbursements.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography
                sx={{ color: "#8b8fa3", fontSize: "0.9rem", fontWeight: 500 }}
              >
                No reimbursements match your filters
              </Typography>
              <Typography sx={{ color: "#b0b4c8", fontSize: "0.8rem", mt: 0.5 }}>
                Try adjusting your search or status filter
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, pl: 3 }}>
                      Employee
                    </TableCell>
                    <TableCell sx={headerCellSx}>Date Range</TableCell>
                    <TableCell sx={headerCellSx}>Purpose</TableCell>
                    <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        textAlign: "center",
                        width: 100,
                      }}
                    >
                      Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReimbursements.map((reimbursement, index) => {
                    const overallStatus = getOverallStatus(reimbursement);
                    const borderColor =
                      statusBorderColor[overallStatus] || "#fccb90";

                    // Date range from expenses
                    const dates = (reimbursement.expenses || [])
                      .map((e) => new Date(e.date))
                      .filter((d) => !isNaN(d));
                    const minDate =
                      dates.length > 0
                        ? new Date(Math.min(...dates)).toLocaleDateString()
                        : "-";
                    const maxDate =
                      dates.length > 0
                        ? new Date(Math.max(...dates)).toLocaleDateString()
                        : "-";
                    const dateRange =
                      minDate === maxDate
                        ? minDate
                        : `${minDate} - ${maxDate}`;

                    return (
                      <React.Fragment key={reimbursement._id}>
                        <TableRow
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(102,126,234,0.03)",
                            },
                            "& td:first-of-type": {
                              borderLeft: `4px solid ${borderColor}`,
                            },
                            ...(expanded[index] && {
                              bgcolor: "rgba(102,126,234,0.02)",
                            }),
                          }}
                          onClick={() => handleExpandClick(index)}
                        >
                          {/* Employee with gradient avatar + ID chip */}
                          <TableCell sx={{ ...bodyCellSx, pl: 2.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Avatar
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #667eea, #764ba2)",
                                  color: "#fff",
                                  width: 40,
                                  height: 40,
                                  fontSize: "0.9rem",
                                  fontWeight: 700,
                                  boxShadow:
                                    "0 3px 10px rgba(102,126,234,0.3)",
                                }}
                              >
                                {reimbursement.employeeName
                                  ?.charAt(0)
                                  .toUpperCase() || "U"}
                              </Avatar>
                              <Box>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: "#1a1a2e",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {reimbursement.employeeName}
                                </Typography>
                                {reimbursement.managerName && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: "#8b8fa3",
                                      mt: 0.2,
                                    }}
                                  >
                                    Mgr: {reimbursement.managerName}
                                  </Typography>
                                )}
                                {reimbursement.employeeId && (
                                  <Chip
                                    label={reimbursement.employeeId}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      height: 20,
                                      fontSize: "0.65rem",
                                      fontWeight: 600,
                                      bgcolor: "#f0f1fa",
                                      color: "#667eea",
                                      borderRadius: "6px",
                                      "& .MuiChip-label": {
                                        px: 1,
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Date Range */}
                          <TableCell sx={bodyCellSx}>
                            <Typography
                              sx={{
                                fontSize: "0.8125rem",
                                color: "#5a5f7a",
                                fontWeight: 500,
                              }}
                            >
                              {dateRange}
                            </Typography>
                          </TableCell>

                          {/* Purpose */}
                          <TableCell sx={bodyCellSx}>
                            <Typography
                              sx={{
                                fontSize: "0.8125rem",
                                color: "#5a5f7a",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {reimbursement.purpose}
                            </Typography>
                          </TableCell>

                          {/* Amount in colored pill */}
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              textAlign: "right",
                            }}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                bgcolor: "#f0f1fa",
                                borderRadius: "10px",
                                px: 1.5,
                                py: 0.5,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.9rem",
                                  background:
                                    "linear-gradient(135deg, #667eea, #764ba2)",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                }}
                              >
                                {"\u20B9"}
                                {reimbursement.totalAmount?.toLocaleString() ??
                                  reimbursement.totalAmount}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Status Chip */}
                          <TableCell
                            sx={{ ...bodyCellSx, textAlign: "center" }}
                          >
                            <StatusChip status={overallStatus} />
                          </TableCell>

                          {/* Expand Button - styled */}
                          <TableCell
                            sx={{ ...bodyCellSx, textAlign: "center" }}
                          >
                            <Tooltip
                              title={
                                expanded[index]
                                  ? "Hide details"
                                  : "View details"
                              }
                              arrow
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: expanded[index]
                                    ? "rgba(102,126,234,0.1)"
                                    : "rgba(139,143,163,0.08)",
                                  color: expanded[index]
                                    ? "#667eea"
                                    : "#8b8fa3",
                                  width: 34,
                                  height: 34,
                                  transition: "all 0.3s ease",
                                  transform: expanded[index]
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                  "&:hover": {
                                    bgcolor: "rgba(102,126,234,0.15)",
                                    color: "#667eea",
                                  },
                                }}
                              >
                                <ExpandMore fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Detail Row */}
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              py: 0,
                              px: 0,
                              borderBottom: expanded[index]
                                ? undefined
                                : "none",
                            }}
                          >
                            <Collapse
                              in={expanded[index]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  p: { xs: 2, sm: 3 },
                                  bgcolor: "#fafbfd",
                                  borderRadius: "0 0 12px 12px",
                                }}
                              >
                                {/* Section Title with icon and gradient underline */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 2.5,
                                    pb: 1.5,
                                    borderBottom: "3px solid transparent",
                                    borderImage:
                                      "linear-gradient(135deg, #667eea, #764ba2) 1",
                                  }}
                                >
                                  <ReceiptLongIcon
                                    sx={{ color: "#667eea", fontSize: 20 }}
                                  />
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "0.9rem",
                                      color: "#1a1a2e",
                                    }}
                                  >
                                    Expense Details
                                  </Typography>
                                  <Chip
                                    label={`${reimbursement.expenses?.length || 0} items`}
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      height: 22,
                                      fontSize: "0.68rem",
                                      fontWeight: 600,
                                      bgcolor: "#f0f1fa",
                                      color: "#667eea",
                                      borderRadius: "6px",
                                    }}
                                  />
                                </Box>

                                <TableContainer
                                  component={Paper}
                                  elevation={0}
                                  sx={{
                                    border: "1px solid #eef0f6",
                                    borderRadius: "12px",
                                    overflowX: "auto",
                                    maxWidth: "100%",
                                    bgcolor: "#fff",
                                  }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={innerHeaderSx}>
                                          Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            minWidth: 140,
                                          }}
                                        >
                                          Description
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            textAlign: "right",
                                          }}
                                        >
                                          Conveyance
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            textAlign: "right",
                                          }}
                                        >
                                          Material
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            textAlign: "right",
                                          }}
                                        >
                                          Other
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            textAlign: "right",
                                          }}
                                        >
                                          Total
                                        </TableCell>
                                        <TableCell sx={innerHeaderSx}>
                                          Note
                                        </TableCell>
                                        <TableCell sx={innerHeaderSx}>
                                          Files
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            minWidth: 150,
                                          }}
                                        >
                                          Status
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...innerHeaderSx,
                                            minWidth: 260,
                                          }}
                                        >
                                          Remark
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {reimbursement.expenses.map(
                                        (expense, eIdx) => (
                                          <TableRow
                                            key={expense._id}
                                            sx={{
                                              bgcolor:
                                                eIdx % 2 === 0
                                                  ? "#fff"
                                                  : "#fafbfe",
                                              "&:hover": {
                                                bgcolor:
                                                  "rgba(102,126,234,0.04)",
                                              },
                                              transition:
                                                "background-color 0.15s ease",
                                            }}
                                          >
                                            <TableCell sx={innerBodySx}>
                                              {new Date(
                                                expense.date
                                              ).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell sx={innerBodySx}>
                                              <Typography
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  color: "#3a3a5c",
                                                  maxWidth: 180,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {expense.description}
                                              </Typography>
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                ...innerBodySx,
                                                textAlign: "right",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {displayAmount(
                                                expense.conveyance
                                              )}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                ...innerBodySx,
                                                textAlign: "right",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {displayAmount(
                                                expense.Material_Purchase
                                              )}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                ...innerBodySx,
                                                textAlign: "right",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {displayAmount(expense.other)}
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                ...innerBodySx,
                                                textAlign: "right",
                                              }}
                                            >
                                              <Typography
                                                component="span"
                                                sx={{
                                                  fontWeight: 700,
                                                  fontSize: "0.82rem",
                                                  background:
                                                    "linear-gradient(135deg, #667eea, #764ba2)",
                                                  WebkitBackgroundClip: "text",
                                                  WebkitTextFillColor:
                                                    "transparent",
                                                }}
                                              >
                                                {displayAmount(expense.total)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell sx={innerBodySx}>
                                              <Typography
                                                sx={{
                                                  fontSize: "0.78rem",
                                                  color: "#8b8fa3",
                                                  maxWidth: 120,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {expense.note || "-"}
                                              </Typography>
                                            </TableCell>
                                            {/* Files as gradient chips */}
                                            <TableCell sx={innerBodySx}>
                                              {expense.files &&
                                              expense.files.length > 0 ? (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  {expense.files.map(
                                                    (file, fileIndex) => (
                                                      <Chip
                                                        key={fileIndex}
                                                        component="a"
                                                        href={file}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        clickable
                                                        icon={
                                                          <AttachFileIcon
                                                            sx={{
                                                              fontSize:
                                                                "14px !important",
                                                              color:
                                                                "#667eea !important",
                                                            }}
                                                          />
                                                        }
                                                        label={`File ${fileIndex + 1}`}
                                                        size="small"
                                                        sx={{
                                                          height: 26,
                                                          fontSize: "0.7rem",
                                                          fontWeight: 600,
                                                          bgcolor: "#f0f1fa",
                                                          color: "#667eea",
                                                          borderRadius: "8px",
                                                          border:
                                                            "1px solid #e0e3f5",
                                                          "&:hover": {
                                                            background:
                                                              "linear-gradient(135deg, #667eea, #764ba2)",
                                                            color: "#fff",
                                                            "& .MuiChip-icon":
                                                              {
                                                                color:
                                                                  "#fff !important",
                                                              },
                                                          },
                                                          transition:
                                                            "all 0.2s ease",
                                                        }}
                                                      />
                                                    )
                                                  )}
                                                </Box>
                                              ) : (
                                                <Typography
                                                  sx={{
                                                    fontSize: "0.78rem",
                                                    color: "#c0c4d6",
                                                  }}
                                                >
                                                  -
                                                </Typography>
                                              )}
                                            </TableCell>
                                            {/* Status change dropdown - styled Select */}
                                            <TableCell sx={innerBodySx}>
                                              <Select
                                                value={expense.status}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  handleStatusChange(
                                                    reimbursement._id,
                                                    expense._id,
                                                    e.target.value
                                                  );
                                                }}
                                                size="small"
                                                sx={{
                                                  fontSize: "0.78rem",
                                                  fontWeight: 600,
                                                  borderRadius: "10px",
                                                  minWidth: 135,
                                                  bgcolor:
                                                    expense.status ===
                                                    "approved"
                                                      ? "rgba(67,233,123,0.08)"
                                                      : expense.status ===
                                                          "disapproved"
                                                        ? "rgba(245,87,108,0.08)"
                                                        : "rgba(252,203,144,0.15)",
                                                  color:
                                                    expense.status ===
                                                    "approved"
                                                      ? "#059669"
                                                      : expense.status ===
                                                          "disapproved"
                                                        ? "#DC2626"
                                                        : "#D97706",
                                                  "& .MuiOutlinedInput-notchedOutline":
                                                    {
                                                      borderColor:
                                                        expense.status ===
                                                        "approved"
                                                          ? "rgba(67,233,123,0.3)"
                                                          : expense.status ===
                                                              "disapproved"
                                                            ? "rgba(245,87,108,0.3)"
                                                            : "rgba(252,203,144,0.4)",
                                                    },
                                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                                    {
                                                      borderColor: "#667eea",
                                                    },
                                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    {
                                                      borderColor: "#667eea",
                                                      borderWidth: "2px",
                                                    },
                                                  "& .MuiSelect-select": {
                                                    py: 0.75,
                                                    pr: 3,
                                                  },
                                                }}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <MenuItem value="pending">
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        bgcolor: "#fccb90",
                                                      }}
                                                    />
                                                    Pending
                                                  </Box>
                                                </MenuItem>
                                                <MenuItem value="approved">
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        bgcolor: "#43e97b",
                                                      }}
                                                    />
                                                    Approved
                                                  </Box>
                                                </MenuItem>
                                                <MenuItem value="disapproved">
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        bgcolor: "#f5576c",
                                                      }}
                                                    />
                                                    Disapproved
                                                  </Box>
                                                </MenuItem>
                                              </Select>
                                            </TableCell>
                                            {/* Remark input + Save button */}
                                            <TableCell sx={innerBodySx}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  gap: 1,
                                                  alignItems: "center",
                                                }}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <TextField
                                                  placeholder="Enter remark..."
                                                  variant="outlined"
                                                  size="small"
                                                  value={
                                                    remarks[expense._id] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleRemarkChange(
                                                      expense._id,
                                                      e.target.value
                                                    )
                                                  }
                                                  sx={{
                                                    flex: 1,
                                                    "& .MuiOutlinedInput-root":
                                                      {
                                                        fontSize: "0.78rem",
                                                        borderRadius: "10px",
                                                        bgcolor: "#f8f9fc",
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                          {
                                                            borderColor:
                                                              "#667eea",
                                                          },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                          {
                                                            borderColor:
                                                              "#667eea",
                                                            borderWidth: "2px",
                                                          },
                                                      },
                                                    "& .MuiOutlinedInput-notchedOutline":
                                                      {
                                                        borderColor: "#eef0f6",
                                                      },
                                                  }}
                                                />
                                                <Tooltip title="Save remark" arrow>
                                                  <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleStatusChange(
                                                        reimbursement._id,
                                                        expense._id,
                                                        expense.status
                                                      );
                                                    }}
                                                    startIcon={
                                                      <SaveOutlinedIcon
                                                        sx={{ fontSize: 16 }}
                                                      />
                                                    }
                                                    sx={{
                                                      background:
                                                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                      textTransform: "none",
                                                      borderRadius: "10px",
                                                      fontSize: "0.75rem",
                                                      fontWeight: 600,
                                                      px: 2,
                                                      py: 0.7,
                                                      whiteSpace: "nowrap",
                                                      boxShadow:
                                                        "0 3px 10px rgba(102,126,234,0.25)",
                                                      "&:hover": {
                                                        background:
                                                          "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                                                        boxShadow:
                                                          "0 4px 14px rgba(102,126,234,0.35)",
                                                      },
                                                    }}
                                                  >
                                                    Save
                                                  </Button>
                                                </Tooltip>
                                              </Box>
                                              {expense.remark && (
                                                <Box
                                                  sx={{
                                                    mt: 1,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    bgcolor: "#f0f1fa",
                                                    borderRadius: "8px",
                                                    borderLeft:
                                                      "3px solid #667eea",
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.72rem",
                                                      color: "#5a5f7a",
                                                    }}
                                                  >
                                                    <strong
                                                      style={{
                                                        color: "#667eea",
                                                      }}
                                                    >
                                                      Remark:
                                                    </strong>{" "}
                                                    {expense.remark}
                                                  </Typography>
                                                </Box>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </PageContainer>
  );
};

export default ReimbursementList;
